import type { Block } from '@vedalib/editor'
import type { CheckboxElementValue } from '@vedalib/editor/lib/elements'
import { CheckboxCondition, CheckboxProgress, CheckboxType } from '@vedalib/editor/lib/elements'
import React, { useMemo } from 'react'

import { genElementValueField } from 'components/Editor/Toolbar/NodeForm/genElementValueField'
import Padding from 'components/Editor/Toolbar/Padding'
import type { IFieldConfig } from 'components/formKit'
import { chainName, FieldSelect, FormField } from 'components/formKit'
import { useFormData } from 'components/formKit/hooks'
import { BlockMode } from 'services/Store/Project/enums'
import { t } from 'services/Translation'
import { getEnumOption } from 'utils/enum'

import type { CheckboxValueFormType } from './checkboxTypes'

const useShowCondition = ({ name }: IFieldConfig<Block>) => {
  const values = useFormData<CheckboxElementValue>(name, 1)
  const hidden = values?.type === CheckboxType.selfCheck
  return useMemo(() => ({ hidden }), [hidden])
}

const useShowType = () => {
  const block = useFormData<Block>()
  const disabled = [BlockMode.cover, BlockMode.start, BlockMode.end, BlockMode.questions].includes(
    block.mode,
  )
  return useMemo(() => ({ disabled }), [disabled])
}

const TYPE = genElementValueField({
  as: FieldSelect<CheckboxType>,
  name: 'type',
  label: t('elements.checkbox.form.type.label'),
  defaultValue: CheckboxType.selfCheck,
  props: {
    options: [
      getEnumOption('CheckboxType', CheckboxType.selfCheck),
      getEnumOption('CheckboxType', CheckboxType.continue),
    ],
  },
  useConfigs: [useShowType],
})

const CONDITION = genElementValueField({
  as: FieldSelect<CheckboxCondition>,
  name: 'condition',
  label: t('elements.checkbox.form.condition.label'),
  defaultValue: CheckboxCondition.last,
  props: {
    options: [
      getEnumOption('CheckboxCondition', CheckboxCondition.none),
      getEnumOption('CheckboxCondition', CheckboxCondition.all),
      getEnumOption('CheckboxCondition', CheckboxCondition.last),
    ],
  },
  useConfigs: [useShowCondition],
})

const PROGRESS = genElementValueField({
  as: FieldSelect<CheckboxProgress>,
  name: 'progress',
  label: t('elements.checkbox.form.progress.label'),
  info: t('elements.checkbox.form.progress.info'),
  defaultValue: CheckboxProgress.all,
  props: {
    options: [
      getEnumOption('CheckboxProgress', CheckboxProgress.any),
      getEnumOption('CheckboxProgress', CheckboxProgress.all),
    ],
  },
})

const CheckboxForm: CheckboxValueFormType = ({ name }) => (
  <Padding bottom gap sides>
    <FormField config={chainName(name, TYPE)} />
    <FormField config={chainName(name, CONDITION)} />
    <FormField config={chainName(name, PROGRESS)} />
  </Padding>
)

export default React.memo(CheckboxForm) as typeof CheckboxForm
